import React from 'react';
import { Button, Modal, TextField } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import './TimePickerModal.scss';
import { useTranslation } from 'react-i18next';

interface TimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTimeChange: (time: Date) => void;
  title: string;
  initialTime?: string;
  timeFormat: '12h' | '24h';
}

const TimePickerModal: React.FC<TimePickerModalProps> = ({
  isOpen,
  onClose,
  onTimeChange,
  title,
  initialTime,
  timeFormat,
}) => {
  const { t } = useTranslation();
  const [selectedTime, setSelectedTime] = React.useState<Dayjs | null>(
    initialTime ? dayjs(`2000-01-01 ${initialTime}`) : null
  );

  React.useEffect(() => {
    if (isOpen && initialTime) {
      setSelectedTime(dayjs(`2000-01-01 ${initialTime}`));
    }
  }, [isOpen, initialTime]);

  const handleTimeChange = (newTime: Dayjs | null) => {
    setSelectedTime(newTime);
  };

  const handleSave = () => {
    if (selectedTime) {
      onTimeChange(selectedTime.toDate());
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedTime(initialTime ? dayjs(`2000-01-01 ${initialTime}`) : null);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} className="time-picker-modal">
      <div className="time-picker-modal__content">
        <h3>🕐 {t('settings.aiAssistant.setTimeTitle', { title })}</h3>
        <div className="time-picker-modal__time-picker">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              ampm={timeFormat === '12h'}
              format={timeFormat === '12h' ? 'h:mm A' : 'HH:mm'}
              slotProps={{
                textField: {
                  className: 'time-picker-modal__time-input',
                  placeholder: t('settings.aiAssistant.selectTime'),
                },
              }}
            />
          </LocalizationProvider>
        </div>
        <div className="time-picker-modal__actions">
          <Button variant="text" onClick={handleClose} className="time-picker-modal__cancel-button">
            {t('settings.common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="time-picker-modal__save-button"
            disabled={!selectedTime}
          >
            {t('settings.common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TimePickerModal;
